// React
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

// UI primitives
import { AllOrdersPage } from '../Sales/OrderManagement';
import { CreateOrderDialog } from '../Sales/OrderManagement/CreateOrder/CreateOrderDialog';
import { SalespersonFilterSheet } from '../Sales/OrderManagement/Sections/SalespersonFilterSheet';
import { OrdersMobileFooter } from '../Sales/OrderManagement/Sections/OrdersMobileFooter';
import { openMobileSidebar } from '../MobileSidebar/mobileSidebarEvents';
import { Toaster } from '@/components/ui/toaster';
import { Page } from '../layouts/Page';
import { PageHeaderTotalLabel, PageHeaderSearchBar } from '../layouts/PageHeader';
import { ResponsivePageHeader } from '../layouts/ResponsivePageHeader';
import { DataTableFooter } from '@components/DataTable/DataTableFooter';

// Hooks and utilities
import { useOrdersRouterSearch } from '@hooks/orders';
import { useOrders } from '@hooks/orders';
import { searchQueryParam } from '@/react/utils/searchQuery';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useTranslation } from '@hooks/useTranslation';
import { SEARCH_BAR_WIDTH } from '../../constants';

// Types
import type { Salesperson, Order } from '@api/orders';
import type { CurrentUser } from '@/react/lib/capabilities';

interface OrderManagementProps {
  allOrdersCount?: number;
  salespersons?: Salesperson[];
  currentUser?: CurrentUser;
  translations?: {
    actions?: Record<string, string>;
    filters?: Record<string, string>;
    filter_menu?: Record<string, string>;
    header?: {
      total_orders?: string;
    };
    search?: {
      placeholder?: string;
      aria_label?: string;
    };
  };
}

const DEFAULT_TRANSLATIONS: OrderManagementProps['translations'] = {};
const DEFAULT_SALESPERSONS: Salesperson[] = [];
const DEFAULT_ACTIONS: Record<string, string> = {};
const DEFAULT_FILTERS: Record<string, string> = {};

export default function OrderManagement({
  allOrdersCount = 0,
  salespersons = DEFAULT_SALESPERSONS,
  currentUser,
  translations = DEFAULT_TRANSLATIONS,
}: OrderManagementProps) {
  const { t } = useTranslation();
  const router = useOrdersRouterSearch();
  const { search } = router;
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState(search.q);
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  // Re-sync local input when URL changes externally (popstate / in-router nav)
  useEffect(() => {
    setSearchInput(search.q);
  }, [search.q]);

  // Sync debounced typing back to URL (page-1 reset is handled by setQuery)
  useEffect(() => {
    if (debouncedSearch !== search.q) {
      router.setQuery(debouncedSearch);
    }
  }, [debouncedSearch, search.q, router]);

  // Auto-select current user's salesperson for individual salespersons only
  // when the URL has no explicit filter yet. Replaces the old
  // initialOverrides path — TanStack's URL is canonical, so we navigate into
  // the auto-selected state once on mount instead of seeding initial
  // useState.
  useEffect(() => {
    if (!currentUser || currentUser.role !== 'salesperson') return;
    if (search.salespersonIds.length > 0) return;
    if (!salespersons.some((sp) => sp.id === currentUser.id)) return;
    router.setSalespersons([String(currentUser.id)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional once-on-mount
  }, []);

  const actionsTranslations = translations?.actions ?? DEFAULT_ACTIONS;
  const filtersTranslations = translations?.filters ?? DEFAULT_FILTERS;

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const handleNewOrder = useCallback(() => {
    setCreateDialogOpen(true);
  }, []);

  // Order detail preview-sheet state — still driven by clicking an order id in the table
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);

  // Mobile salesperson filter bottom sheet
  const [salespersonSheetOpen, setSalespersonSheetOpen] = useState(false);

  // Client-side navigation to the newly created order's expanded detail view.
  // `/orders/$id/details` lives in this section's router tree, so we transition
  // via TanStack rather than a full document reload. The details route fetches
  // the order through `useOrderRaw` (React Query), showing a brief skeleton —
  // we deliberately don't seed the cache: `createOrder` and `getOrder` return
  // the same `Order` type but from different serializers, and `useOrderRaw`
  // pins `initialData` for 30s, which would surface the create-shape stale.
  const handleOrderCreated = useCallback(
    (order: Order) => {
      navigate({ to: '/orders/$id/details', params: { id: String(order.id) } });
    },
    [navigate],
  );

  const { data, isLoading, isFetching } = useOrders({
    order_statuses: search.orderStatuses.length ? search.orderStatuses : undefined,
    salesperson_ids: search.salespersonIds.length ? search.salespersonIds : undefined,
    q: searchQueryParam(debouncedSearch),
    page: search.page,
    perPage: search.perPage,
    sort: search.sort,
    direction: search.direction,
  });

  const totalCount = data?.totalCount ?? 0;
  const rawOrders = data?.data ?? [];
  const totalPages = Math.max(1, Math.ceil(totalCount / search.perPage));

  // Optimistic client-side filtering so the table updates instantly on filter toggle
  const salespersonIdSet = useMemo(
    () => new Set(search.salespersonIds.map(Number)),
    [search.salespersonIds]
  );
  const orderStatusSet = useMemo(
    () => new Set(search.orderStatuses),
    [search.orderStatuses]
  );
  const orders = useMemo(() => {
    if (salespersonIdSet.size === 0 && orderStatusSet.size === 0) return rawOrders;
    return rawOrders.filter((o) => {
      if (salespersonIdSet.size > 0 && !(o.salesperson && salespersonIdSet.has(o.salesperson.id))) {
        return false;
      }
      return orderStatusSet.size === 0 || orderStatusSet.has(o.order_status);
    });
  }, [rawOrders, salespersonIdSet, orderStatusSet]);

  // Row click → navigate to the order details route. Plain href so the
  // TanStack router intercepts when AppRouter owns the destination and Turbo
  // takes it otherwise.
  const handleRowClick = useCallback((order: { id: number }) => {
    window.location.href = `/orders/${order.id}`;
  }, []);

  const transformedTranslations = useMemo(() => ({
    toolbar: {
      salesperson: filtersTranslations.salesperson,
      new_order: actionsTranslations.new_order,
    },
  }), [filtersTranslations, actionsTranslations]);

  return (
    <>
      <Toaster />
      <Page>
        <Page.Header className="py-6 md:py-0">
          <ResponsivePageHeader
            mobile={{
              title: t('title'),
              count: isLoading ? '…' : totalCount.toLocaleString(),
              searchValue: searchInput,
              onSearchChange: setSearchInput,
              searchPlaceholder: translations?.search?.placeholder ?? t('search.placeholder'),
              searchAriaLabel: translations?.search?.aria_label ?? t('search.aria_label'),
              openNavigationAriaLabel: t('header.open_navigation'),
              openSearchAriaLabel: t('header.open_search'),
              closeSearchAriaLabel: t('header.close_search'),
              onOpenNavigation: openMobileSidebar,
            }}
          >
            <div className="shrink-0">
              <PageHeaderTotalLabel
                count={allOrdersCount}
                label={translations?.header?.total_orders ?? 'Total orders (%{count})'}
              />
            </div>
            <div
              className="flex-1 min-w-0 flex justify-center"
              style={{ flexBasis: SEARCH_BAR_WIDTH }}
            >
              <PageHeaderSearchBar
                value={searchInput}
                onChange={setSearchInput}
                placeholder={translations?.search?.placeholder ?? t('search.placeholder')}
                ariaLabel={translations?.search?.aria_label ?? t('search.aria_label')}
              />
            </div>
            <div
              className="min-w-0 invisible"
              aria-hidden="true"
              style={{ flexShrink: 99 }}
            >
              <PageHeaderTotalLabel
                count={allOrdersCount}
                label={translations?.header?.total_orders ?? 'Total orders (%{count})'}
              />
            </div>
          </ResponsivePageHeader>
        </Page.Header>
        <Page.Body>
          <AllOrdersPage
            totalCount={totalCount}
            salespersons={salespersons}
            selectedSalespersonIds={search.salespersonIds}
            onSalespersonChange={router.setSalespersons}
            selectedOrderStatuses={search.orderStatuses}
            onOrderStatusesChange={router.setOrderStatuses}
            onNewOrder={handleNewOrder}
            translations={transformedTranslations}
            isLoading={isLoading}
            isFetching={isFetching}
            orders={orders}
            onRowClick={handleRowClick}
            selectedOrderId={selectedOrderId}
            onSelectedOrderChange={setSelectedOrderId}
            currentUser={currentUser}
            pagination={{
              page: Math.min(search.page, totalPages),
              totalPages,
              onPageChange: router.setPage,
            }}
          />
        </Page.Body>
        <Page.Footer className="flex">
          <OrdersMobileFooter
            className="md:hidden"
            salespersonLabel={filtersTranslations.salesperson}
            onOpenSalespersonFilter={() => setSalespersonSheetOpen(true)}
          />
          <DataTableFooter
            currentPage={Math.min(search.page, totalPages)}
            totalPages={totalPages}
            totalCount={totalCount}
            perPage={search.perPage}
            onPageChange={router.setPage}
            onPerPageChange={router.setPerPage}
            className="hidden md:flex w-full flex-col justify-center h-full px-6 sm:px-8 lg:px-16"
          />
        </Page.Footer>
      </Page>

      <SalespersonFilterSheet
        open={salespersonSheetOpen}
        onOpenChange={setSalespersonSheetOpen}
        salespersons={salespersons}
        selectedIds={search.salespersonIds}
        onSelectionChange={router.setSalespersons}
        title={filtersTranslations.salesperson}
      />

      <CreateOrderDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={handleOrderCreated}
        salespersons={salespersons}
        currentUser={currentUser}
      />
    </>
  );
}
